import { profileEntryLines } from './profileEntryLines';
import { DIVISION_OPTIONS, NUMBER_REQUIRED_ORGS } from '../constants/professionalRegistry.js';
import {
  DYNAMIC_OPTION_TYPES, OTHER_OPTION, LANGUAGES, MENTAL_HEALTH_SPECIALITIES, SERVICE_FORMAT_OPTIONS,
} from '../constants/profileOptions.js';

// "Auto-fill from your web page": the backend reads one public page and suggests
// profile values; these helpers decide which suggestions are offered. Rules
// Existing profile fields are kept. An explicitly reviewed registry switch
// replaces that registry draft as a group. Qualification suggestions are drafts, never verified credentials.
// Uploads and certificate expiry are kept outside this flow.

export const REGISTRY_KEYS = ['association', 'first_name', 'last_name', 'registered_number'];

export const registryPrefillFields = (state = {}) => [
  ['association', 'Psychological Association / Psychiatrist Registry'],
  ['first_name', 'Registry first and middle name'],
  ['last_name', 'Registry last name'],
  ['registered_number', 'Registry member number'],
].map(([key, label]) => ({ key, label, section: 'Professional Qualifications', current: state[key] || '', disabled: state.loading !== false || state.locked }));

export const ABOUT_MAX_CHARS = 200; // the About me field's own cap in Settings and onboarding

export const isBlankValue = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
};

const displayValue = (value) => (Array.isArray(value) ? value.join(', ') : String(value));

/**
 * Turn the backend's suggestions into review rows.
 *
 * `fields` describes the page: [{ key, label, section, current }], where `key` is
 * the suggestion's key in the backend payload and `current` is the field's value
 * right now. A row is offered only when the field is empty and the suggestion
 * isn't. Order follows `fields`, so rows read in the page's own order.
 */
export function buildPrefillRows(suggestions, fields) {
  const data = suggestions || {};
  return (fields || []).flatMap((field) => {
    let value = field.key === 'about' ? (data.about_original || data.about_summary || data.about) : data[field.key];
    if (['university', 'professional_qualification'].includes(field.key)) value = profileEntryLines(value).join('\n');
    if (field.key === 'about' && typeof value === 'string') value = value.slice(0, ABOUT_MAX_CHARS);
    const switchingRegistry = REGISTRY_KEYS.includes(field.key) && data.association &&
      fields.find(item => item.key === 'association')?.current &&
      fields.find(item => item.key === 'association')?.current !== data.association;
    if (field.disabled || isBlankValue(value) || (field.key !== 'association' && !switchingRegistry && !isBlankValue(field.current))) return [];
    if (field.key === 'association' && value === field.current) return [];
    if (REGISTRY_KEYS.includes(field.key)) {
      const association = DIVISION_OPTIONS.find(option => option.label === data.association);
      const currentAssociation = fields.find(item => item.key === 'association')?.current;
      if (!association) return [];
      // Dependent fields from another registry are replaced only as part of the reviewed switch.
      if (currentAssociation && currentAssociation !== association.label && field.key !== 'association' &&
          fields.find(item => item.key === 'association')?.disabled) return [];
      if (field.key === 'registered_number' && !NUMBER_REQUIRED_ORGS.includes(association.value)) return [];
    }
    return [{ key: field.key, label: field.label, section: field.section, value, display: displayValue(value),
      ...(REGISTRY_KEYS.includes(field.key) ? { registryAssociation: data.association, registrySnapshot: Object.fromEntries(fields.filter(item => REGISTRY_KEYS.includes(item.key)).map(item => [item.key, item.current || ''])) } : {}),
    }];
  });
}

/**
 * The picker lists the backend may choose from, keyed by its field names. The
 * dynamic lists come from loadProfileOptions (Settings appends the "Other"
 * pseudo-option, which is never a real value).
 */
export function buildPrefillOptionLists(optionsByType = {}) {
  const dynamic = (type) => (optionsByType[type] || []).filter((label) => label && label !== OTHER_OPTION);
  return {
    practice_type: MENTAL_HEALTH_SPECIALITIES,
    association: DIVISION_OPTIONS.map(option => option.label),
    languages: LANGUAGES,
    service_formats: SERVICE_FORMAT_OPTIONS,
    intervention_approaches: dynamic(DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES),
    assessment_types: dynamic(DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES),
    preferred_case_types: dynamic(DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES),
    client_groups: dynamic(DYNAMIC_OPTION_TYPES.CLIENT_GROUPS),
  };
}

/**
 * Check a pasted link before calling the backend (which re-checks it).
 * Returns { url } or { error } with the message shown under the field.
 */
export function checkPrefillUrl(input) {
  let url = String(input || '').trim();
  if (!url) return { error: 'Paste a link first.' };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) url = `https://${url}`;
  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return { error: 'Enter a full link starting with https://' };
  }
  if (parsed.protocol !== 'https:' || !parsed.hostname.includes('.') || parsed.username || parsed.password) {
    return { error: 'Enter a full link starting with https://' };
  }
  return { url };
}
